import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Tag, Search } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

const PAGE_SIZE = 25;

const AdminDiscountCodes = () => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ code: "", type: "percentage" as "percentage" | "fixed", value: "", min_order: "", max_uses: "", expires_at: "" });
  const qc = useQueryClient();

  const { data: codes, isLoading } = useQuery({
    queryKey: ["admin-discount-codes"],
    queryFn: async () => {
      const { data, error } = await supabase.from("discount_codes").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const filtered = useMemo(() => {
    if (!codes) return [];
    if (!search) return codes;
    return codes.filter((c) => c.code.toLowerCase().includes(search.toLowerCase()));
  }, [codes, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const paged = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  const handleCreate = async () => {
    if (!form.code || !form.value) { toast.error("Code and value are required"); return; }
    setSaving(true);
    try {
      const { error } = await supabase.from("discount_codes").insert({
        code: form.code.toUpperCase(),
        type: form.type,
        value: parseFloat(form.value),
        min_order: form.min_order ? parseFloat(form.min_order) : 0,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        expires_at: form.expires_at || null,
      });
      if (error) throw error;
      qc.invalidateQueries({ queryKey: ["admin-discount-codes"] });
      toast.success("Discount code created");
      setDialogOpen(false);
      setForm({ code: "", type: "percentage", value: "", min_order: "", max_uses: "", expires_at: "" });
    } catch (e: any) {
      toast.error(e.message || "Failed to create code");
    } finally { setSaving(false); }
  };

  const toggleActive = async (id: string, active: boolean) => {
    const { error } = await supabase.from("discount_codes").update({ active: !active }).eq("id", id);
    if (error) { toast.error("Update failed"); return; }
    qc.invalidateQueries({ queryKey: ["admin-discount-codes"] });
    toast.success(active ? "Code deactivated" : "Code activated");
  };

  const deleteCode = async (id: string) => {
    if (!confirm("Delete this discount code?")) return;
    const { error } = await supabase.from("discount_codes").delete().eq("id", id);
    if (error) { toast.error("Delete failed"); return; }
    qc.invalidateQueries({ queryKey: ["admin-discount-codes"] });
    toast.success("Code deleted");
  };

  const selectStyle = "h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold">Discount Codes</h1>
          <p className="text-sm text-muted-foreground font-body">{filtered.length} codes</p>
        </div>
        <Button onClick={() => setDialogOpen(true)}><Plus className="w-4 h-4 mr-1.5" />New Code</Button>
      </div>

      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input placeholder="Search codes…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} className="pl-10" />
      </div>

      {isLoading ? (
        <div className="text-muted-foreground text-center py-20">Loading…</div>
      ) : (
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/50">
                  <th className="text-left px-4 py-3 font-body font-medium text-muted-foreground">Code</th>
                  <th className="text-left px-4 py-3 font-body font-medium text-muted-foreground">Discount</th>
                  <th className="text-left px-4 py-3 font-body font-medium text-muted-foreground hidden md:table-cell">Min Order</th>
                  <th className="text-left px-4 py-3 font-body font-medium text-muted-foreground hidden md:table-cell">Uses</th>
                  <th className="text-left px-4 py-3 font-body font-medium text-muted-foreground">Status</th>
                  <th className="text-left px-4 py-3 font-body font-medium text-muted-foreground hidden sm:table-cell">Expires</th>
                  <th className="text-left px-4 py-3 font-body font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((c) => (
                  <tr key={c.id} className="border-b border-border hover:bg-secondary/30 transition-colors">
                    <td className="px-4 py-3 font-body font-semibold font-mono">{c.code}</td>
                    <td className="px-4 py-3 font-body">{c.type === "percentage" ? `${c.value}%` : `£${c.value.toFixed(2)}`}</td>
                    <td className="px-4 py-3 font-body text-muted-foreground hidden md:table-cell">{c.min_order ? `£${c.min_order.toFixed(2)}` : "—"}</td>
                    <td className="px-4 py-3 font-body hidden md:table-cell">{c.used_count}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => toggleActive(c.id, c.active)} className={`inline-flex items-center gap-1.5 text-xs font-body font-medium ${c.active ? "text-green-600" : "text-muted-foreground"}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${c.active ? "bg-green-500" : "bg-muted-foreground"}`} />
                        {c.active ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="px-4 py-3 font-body text-muted-foreground text-xs hidden sm:table-cell">{c.expires_at ? format(new Date(c.expires_at), "dd MMM yyyy") : "Never"}</td>
                    <td className="px-4 py-3">
                      <Button size="icon" variant="ghost" onClick={() => deleteCode(c.id)} className="h-8 w-8"><Trash2 className="w-4 h-4 text-destructive" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-12 text-muted-foreground font-body"><Tag className="w-10 h-10 mx-auto mb-3 opacity-40" />No discount codes</div>
          )}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border">
              <p className="text-sm text-muted-foreground font-body">Page {safePage + 1} of {totalPages}</p>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" disabled={safePage === 0} onClick={() => setPage(safePage - 1)}>Prev</Button>
                <Button size="sm" variant="ghost" disabled={safePage >= totalPages - 1} onClick={() => setPage(safePage + 1)}>Next</Button>
              </div>
            </div>
          )}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display">New Discount Code</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="font-body">Code</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. SUMMER20" className="uppercase" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-body">Type</Label>
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as any })} className={`w-full ${selectStyle}`}>
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed (£)</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label className="font-body">Value</Label>
                <Input type="number" min="0" step="0.01" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder={form.type === "percentage" ? "20" : "5.00"} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-body">Min Order (£)</Label>
                <Input type="number" min="0" step="0.01" value={form.min_order} onChange={(e) => setForm({ ...form, min_order: e.target.value })} placeholder="Optional" />
              </div>
              <div className="space-y-2">
                <Label className="font-body">Max Uses</Label>
                <Input type="number" min="1" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} placeholder="Unlimited" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="font-body">Expires At</Label>
              <Input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving}>{saving ? "Creating…" : "Create Code"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDiscountCodes;
